import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError } from "@/api/client";
import { CaptchaField, useCaptcha } from "@/components/Captcha";
import { useSession } from "@/state/session";
import type { SignUpOutcome } from "@/state/session";

export function SignUp() {
  const { signUp } = useSession();
  const navigate = useNavigate();
  const captcha = useCaptcha("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set when the account exists but is waiting on a confirmation click. The
  // form is replaced rather than left on screen: re-submitting it would only
  // ever say "that address is taken", which is a confusing thing to tell
  // somebody who just used it successfully.
  const [pending, setPending] = useState<SignUpOutcome | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const outcome = await signUp(email, password, displayName, captcha.fields());
      if (outcome.verificationRequired) setPending(outcome);
      else navigate("/home");
    } catch (cause) {
      setError(
        cause instanceof ApiError ? (cause.fieldMessage ?? cause.message) : "Could not create the account",
      );
    } finally {
      setBusy(false);
      // Every exit path, including success. The token is spent on the attempt
      // whether it succeeded or not; a stale image is an unsolvable one.
      void captcha.reload();
    }
  }

  if (pending) {
    return (
      <main className="page container" style={{ display: "grid", placeItems: "center" }}>
        <div className="panel panel--narrow" style={{ marginTop: 80 }}>
          <h1 style={{ marginBottom: 6 }}>Check your email</h1>
          <p style={{ color: "var(--paper-300)", fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
            {pending.emailSent ? (
              <>
                We sent a confirmation link to <strong>{pending.email}</strong>. Open it and your
                account is ready — you will be signed in automatically.
              </>
            ) : (
              <>
                Your account was created, but we could not send the confirmation email to{" "}
                <strong>{pending.email}</strong> just now.
              </>
            )}
          </p>
          {!pending.emailSent && (
            <div className="alert alert--error" style={{ marginTop: 16 }}>
              The confirmation email could not be sent. Nothing is lost — request another one.
            </div>
          )}
          <p style={{ color: "var(--paper-400)", fontSize: 13, lineHeight: 1.6 }}>
            The link works once and expires in 24 hours. If it does not arrive, check your spam
            folder, then{" "}
            <Link to="/verify-email" style={{ color: "var(--accent)" }}>
              send it again
            </Link>
            .
          </p>
          <Link className="btn btn--ghost btn--block" to="/signin" style={{ marginTop: 18 }}>
            Back to sign in
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page container" style={{ display: "grid", placeItems: "center" }}>
      <form className="panel panel--narrow" onSubmit={submit} style={{ marginTop: 80 }}>
        <h1 style={{ marginBottom: 6 }}>Create your account</h1>
        <p style={{ color: "var(--paper-400)", fontSize: 13, marginTop: 0, marginBottom: 22 }}>
          Two weeks free. Cancel whenever you like.
        </p>
        {error && <div className="alert alert--error">{error}</div>}
        <div className="field">
          <label htmlFor="name">Name</label>
          <input id="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <span style={{ fontSize: 12, color: "var(--paper-400)" }}>
            We will send a confirmation link here.
          </span>
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <span style={{ fontSize: 12, color: "var(--paper-400)" }}>At least 10 characters.</span>
        </div>
        <CaptchaField state={captcha} />
        <button className="btn btn--accent btn--block" type="submit" disabled={busy}>
          {busy ? "Creating…" : "Start watching"}
        </button>
        <p style={{ marginTop: 18, color: "var(--paper-400)", fontSize: 13 }}>
          Already have an account? <Link to="/signin" style={{ color: "var(--accent)" }}>Sign in</Link>
        </p>
      </form>
    </main>
  );
}
